import DanaMenuPanel from "@dana/Components/DanaMenu/DanaMenuPanel";
import { elevation, themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, fontWeights, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import { getPagesByParent } from "@insite/client-framework/Services/ContentService";
import translate from "@insite/client-framework/Translate";
import { PageModel } from "@insite/client-framework/Types/PageProps";
import { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import isMobile from "ismobilejs";
import React from "react";
import styled, { ThemeConsumer, ThemeProps, withTheme } from "styled-components";

export interface MappedLink {
    // expanded from content-library/src/widgets/header/MainNavigation.tsx
    title: string;
    url?: string;
    type?: string;
    clickableProps?: ClickableProps;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu" | "CustomLinkList";
    children?: MappedLink[];
    excludeFromNavigation?: boolean;
    iconProps?: IconPresentationProps;
}

export interface MenuPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<MenuPresentationProps>;
        menu?: StyledProp<MenuPresentationProps>;
        menuItem?: StyledProp<MenuPresentationProps>;
    };
    /** Props passed to the menu items.
     * @themable */
    menuItemTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu item Clickable.
     * @themable */
    menuItemClickableProps?: ClickablePresentationProps;
    /** Props to be passed to the right chevron arrow for menu items with children.
     * @themable */
    moreIconProps?: IconPresentationProps;
}

type MenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /** Maximum number of menus that will appear through submenu cascading. */
        maxDepth?: number;
        /** The nested child structure that will be rendered by the menu. */
        menuItems: MappedLink[];
        /** The element that triggers the menu. */
        menuTrigger: React.ReactElement<{ id: string; className: string, "aria-expanded": boolean, role: string }>;
        /** Width of the menu and submenus in pixels. */
        width?: number;
        /** Allows forcing the state of this menu to be open */
        isOpen?: true;
        /** Session boolean value that decides whether to show Change Customer Link */
        displayChangeCustomerLink?: boolean;
        parentCategoryName?: string;
        defaultLanguageId?: any;
        currentLanguageId?: any;
        parentNodeId?: string;
    }
>;

/** MenuComponentProps with defaulted properties now listed as not-optional, plus theme. */
export type MenuProps = MenuComponentProps & MenuPresentationProps & ThemeProps<BaseTheme>;

type DanaMenuStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
    };

// eslint-disable-next-line no-unexpected-multiline
const MenuStyle = styled.ul<DanaMenuStyleProps>`
    background: ${getColor("common.background")};
    z-index: ${getProp("theme.zIndex.menu")};
    box-shadow: ${elevation.hover};
    width: ${getProp("width", 303)}px;
    list-style: none;
    position: absolute;
    margin: 0;
    padding: 0;
    display: none;
    top: calc(100% + 20px);
    padding: 16px 0 24px 0;
    border: 1px solid ${themeBackgrounds.lightGray};
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;

    &.d-none {
        display: none;
    }

    &.d-block {
        display: block;
    }

    .parent-title {
        color: ${themeBackgrounds.darkGray};
        padding: 10px 40px 18px;
        position: relative;
        font-size: ${fontSizes.small};
        line-height: ${lineHeights.small};
        font-weight: ${fontWeights.semiBold};
        margin-bottom: 8px;

        &:empty {
            display: none;
        }

        &::after {
            content: "";
            position: absolute;
            bottom: 0;
            height: 1px;
            background: ${themeBackgrounds.lightGray};
            width: calc(100% - 80px);
            left: 40px;
        }
    }

    ${injectCss}
`;

type MenuWrapperProps = { canBeOpen: boolean; isOpen?: boolean } & InjectableCss;

const MenuWrapper = styled.nav<MenuWrapperProps>`
    position: relative; /* stylelint-disable */
    ${({ isOpen }) =>
        isOpen &&
        `& > ${MenuStyle} {
                display: block;
                height: auto;
            }
    `}
    ${injectCss}
`;


const MenuItemText = styled(Typography as any)<{ hasChildren?: boolean }>`
    color: ${getColor("text.main")};
    display: block;
    box-sizing: border-box;
    ${({ hasChildren }: { hasChildren?: boolean }) => hasChildren && "width: 100%;"}
`;

const trigger = React.createRef<HTMLElement>();

interface MenuState {
    canBeOpen: boolean;
    isMobile: boolean;
    shouldRender: boolean;
    menuLinks: MappedLink[] | undefined;
    width: number;
}

/**
 * A cascading menu that renders a defined number of sub-menus.
 */
class DanaMenu extends React.Component<MenuProps, MenuState> {
    menuWrapperRef = React.createRef<HTMLElement>();

    static defaultProps: Partial<MenuProps> = {
        maxDepth: 3,
        width: 303,
    };

    state: MenuState = {
        canBeOpen: false,
        isMobile: false,
        shouldRender: true,
        menuLinks: this.props.menuItems,
        width: 0,
    };
    

    updateWidth = () => {
        this.setState({ width: window.innerWidth });
      };

    componentDidMount() {
        let customLinks: MappedLink[] | undefined;
        customLinks = { ...this.props.menuItems };
       
        
        document.addEventListener("click", this.globalClickListener);
        this.setState({
            isMobile: isMobile().any,
        });

        window.addEventListener("resize", () => {
            window.setTimeout(() => {
                this.setState({
                    isMobile: isMobile().any,
                });
            }, 50);
        });

        window.addEventListener("popstate", this.closeDropdown);
        document.addEventListener("keydown", this.handleKeyDown);
        if (typeof window !== "undefined") {
            this.setState({ width: window.innerWidth });
            window.addEventListener("resize", this.handleResize);
          }
    }

    componentWillUnmount() {
        window.removeEventListener("click", this.globalClickListener);

        window.removeEventListener("resize", () => {
            window.setTimeout(() => {
                this.setState({
                    isMobile: isMobile().any,
                });
            }, 50);
        });

        window.removeEventListener("popstate", this.closeDropdown);
        document.removeEventListener("keydown", this.handleKeyDown);
        if (typeof window !== "undefined") {
            window.removeEventListener("resize", this.handleResize);
          }
    }

    handleResize = () => {
        this.setState({ width: window.innerWidth });
      };

    handleKeyDown = (e: KeyboardEvent) => {
    
        if (e.key === "Escape") {
            this.closeDropdown();
        } else if (e.key === "Tab") {
            this.trapFocus(e);
            const triggerButton = this.menuWrapperRef.current?.querySelector("ul li:nth-child(2) a") as HTMLElement;
            if (triggerButton?.classList.contains("subMenuList")) {
                triggerButton.focus();
                triggerButton.classList.remove("subMenuList");
            }
        }
    };

    trapFocus = (e: KeyboardEvent) => {
        const menuWrapper = this.menuWrapperRef.current;

        if (!menuWrapper) {
            return;
        }

        const focusedElement = document.activeElement as HTMLElement;
        const currentSubMenu = focusedElement.closest(".menu-dropdown, .account-dropdown");

        if (!currentSubMenu) {
            return;
        }

        const focusableElements = currentSubMenu.querySelectorAll<HTMLElement>(
            'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );

        if (!focusableElements || focusableElements.length === 0) {
            return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
        }
    };

    closeMenu = () => {
        // On Mobile, the below logic
        // where we switch the canBeOpen state from false to true
        // causes the mobile experience to not function well
        // due to Mobile not doing hover functionality

        // Solution:
        // I prevent the boolean toggle when we detect mobile

        this.setState({ canBeOpen: this.state.isMobile }, () =>
            setTimeout(() => {
                // The make the all browsers act the same on item click.
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
                this.setState({ canBeOpen: true });
            }, 100),
        );
    };
    

    closeMenuWithoutChildren = (hasMenuChildren?: boolean) => {
        // This is for high resolution mobile users.
        // So that the menu closes on nav items without children
        if (!hasMenuChildren) {
            this.setState(
                {
                    shouldRender: false,
                },
                () => {
                    this.setState({ shouldRender: true });
                },
            );
        }
    };

    closeDropdown = () => {
        const dropdown = document.querySelector(".current-active-dropdown");
        const activeMenu = document.querySelector(".menu-active");
        dropdown?.classList.remove("current-active-dropdown");
        dropdown?.classList.remove("d-block");
        activeMenu?.classList.remove("menu-active");
        const triggerButton = activeMenu?.querySelector(".trigger");
        if (triggerButton) {
            triggerButton.setAttribute("aria-expanded", "false");
        }
    };

    globalClickListener = () => {
        this.closeDropdown();
        this.setState({ canBeOpen: false }, () => {
            window.removeEventListener("click", this.globalClickListener);
        });
    };

    
    menuWrapperMouseLeaveHandler = () => {
        if (!this.menuWrapperRef.current) {
            return;
        }

        this.menuWrapperRef.current.blur();

        const menuTrigger = this.menuWrapperRef.current.children[0] as HTMLElement;
        if (!menuTrigger) {
            return;
        }

        menuTrigger.blur();
    };

    render() {
        const { width } = this.state;
        return (
            <ThemeConsumer>
                {theme => {
                    const { menuItems, menuTrigger, isOpen, mergeCss, ...otherProps } = this.props;
                    const { applyStyledProp } = applyPropBuilder(
                        { ...otherProps, theme },
                        { component: "menu" },
                    );
                    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.menu?.defaultProps?.mergeCss;
                    const renderMenuItems = (
                        childMenuItems: MappedLink[],
                        currentDepth: number,
                        parentTitle: string,
                    ) => {
               
                        return (
                            <>
                                {width >= 992 && (
                                    <MenuStyle
                                        width={otherProps.width}
                                        css={applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
                                    >
                                        <p className="parent-title">
                                            {parentTitle && parentTitle !== ""
                                                ? translate(parentTitle)
                                                : otherProps.parentCategoryName}
                                        </p>

                                        <DanaMenuPanel menuItems={childMenuItems}></DanaMenuPanel>
                                    </MenuStyle>
                                )}
                            </>
                        );
                    };

                    
                    const descriptionId = otherProps.descriptionId || uniqueId();
                    return (
                        <>
                            <MenuWrapper
                                ref={this.menuWrapperRef}                            
                                isOpen={isOpen}
                                onMouseLeave={this.menuWrapperMouseLeaveHandler}
                                css={applyStyledProp(["cssOverrides", "wrapper"], resolvedMergeCss)}
                                {...{ "aria-labelledby": descriptionId }}
                                {...otherProps}
                            >
                                {React.cloneElement(menuTrigger, {
                                    id: descriptionId,
                                    className: "trigger",
                                    "aria-expanded": !!isOpen,
                                    role: "button",
                                })}
                                {renderMenuItems(menuItems!, 1, "")}
                            </MenuWrapper>
                            <span ref={trigger} tabIndex={-1} />
                        </>
                    );
                }}
            </ThemeConsumer>
        );
    }
}

/** @component */
export default withTheme(DanaMenu);

export { MenuItemText };
